import { isWindowDefined } from "../element/isWindowDefined";

/**
 * Thin, SSR-safe wrapper around `sessionStorage`.
 *
 * Session storage is scoped to the browsing tab/session and is cleared when the
 * tab is closed, so it is a strictly smaller at-rest footprint than
 * `localStorage` for sensitive values (auth session credential, applicant
 * financial PII). It is used to keep those values out of long-lived
 * `localStorage`, narrowing the XSS blast radius (SMA-2668 / SMA-3172).
 *
 * All accesses are guarded: `sessionStorage` can throw (private-mode quota,
 * disabled storage) and is undefined during SSR.
 */
export const sessionStore = {
  get(key: string): string | null {
    if (!isWindowDefined()) {
      return null;
    }
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Writes a value to `sessionStorage`.
   * @returns `true` when the value was durably written, `false` when the write
   *   was skipped (SSR) or failed (quota / disabled storage). Callers can use
   *   this to avoid discarding other durable copies after a failed write.
   */
  set(key: string, value: string): boolean {
    if (!isWindowDefined()) {
      return false;
    }
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch {
      // Ignore write failures (quota / disabled storage) — the caller keeps the
      // value in memory regardless.
      return false;
    }
  },

  remove(key: string): void {
    if (!isWindowDefined()) {
      return;
    }
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Ignore.
    }
  },
};
